package com.xorrus.trading.lot.relayclient.model

import kotlinx.datetime.LocalTime
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * MMS — konkrete Typisierung (Spec §3.5, normativ).
 *
 * Underlying: MMS handelt immer SPX. Das Underlying ist eine fixe Strategie-Konstante —
 * weder Parameter noch Option noch Statusfeld; UI-Anzeigen dürfen „SPX" fest rendern.
 */

val MMS_STRATEGY_ID = StrategyId("mms")

/**
 * v2: Leg-Felder, TP/SL und maxSignals sind nullable — die UI-Semantik „Feld leer"
 * (inaktives Bein, kein TP/SL, unbegrenzte Signale) wird 1:1 abgebildet statt mit
 * Ersatzwerten aufgefüllt. v1-JSON (alles non-null) bleibt dekodierbar; v1-Leser
 * können v2-Params mit nulls nicht lesen → Versionssprung (UNSUPPORTED_SCHEMA).
 *
 * v3: `maxDeviation` ist keine prozentuale Abweichung vom Zielpreis mehr, sondern eine
 * **absolute Toleranz** (Prämienbetrag, analog Smart Entry Options). Strukturell identisch
 * zu v2 — der Versionssprung existiert nur wegen der Bedeutungsänderung: ein v2-Sender
 * würde 25 (%) schicken, ein v3-Leser läse 25 Punkte Toleranz.
 */
const val MMS_SCHEMA_VERSION = 3

// ---- Parameter (vier Beine, zwei Formen) ------------------------------------------

@Serializable
data class LongLegParams(
    /** Zielpreis (XBigNum-Herkunft); null = Bein nicht konfiguriert */
    val targetPrice: Decimal? = null,
    /** Toleranz — absolute Abweichung vom Zielpreis (Prämienbetrag, seit v3); null = Bein nicht konfiguriert */
    val maxDeviation: Double? = null,
)

@Serializable
data class ShortLegParams(
    /** Zielpreis; null = Bein nicht konfiguriert */
    val targetPrice: Decimal? = null,
    /** Toleranz — absolute Abweichung vom Zielpreis (Prämienbetrag, seit v3); null = Bein nicht konfiguriert */
    val maxDeviation: Double? = null,
    /** TP — relativer Offset zum Fill-Preis, > 0; null = kein TP */
    val takeProfit: Double? = null,
    /** SL — relativer Offset zum Fill-Preis, > 0; null = kein SL */
    val stopLoss: Double? = null,
)

@Serializable
data class ThresholdTriple(val t1: Double, val t2: Double, val t3: Double)

@Serializable
data class PriceIndexParams(
    /** PreisIndex Period */
    val period: Int,
    /** PreisIndex SMA */
    val sma: Int,
    /** Long Schwelle 1–3 */
    val longThresholds: ThresholdTriple,
    /** Short Schwelle 1–3 */
    val shortThresholds: ThresholdTriple,
)

@Serializable
@SerialName("mms")
data class MmsParams(
    /** Konto (dynamische Auswahl, [MmsOptions]) */
    val accountId: AccountId,
    /** An Börse übertragen */
    val transmitToExchange: Boolean,
    /** Anzahl 1..X */
    val quantity: Int,
    /** Longs automatisch öffnen */
    val autoOpenLongs: Boolean,
    val longPut: LongLegParams,
    val longCall: LongLegParams,
    val shortPut: ShortLegParams,
    val shortCall: ShortLegParams,
    /** Bear / Bull */
    val zone: Zone,
    /** Beginn Zone */
    val zoneStart: Decimal,
    /** Ende Zone */
    val zoneEnd: Decimal,
    /** Startzeit (NY-Zeit, Interpretation wie MMSStrategy) */
    val startTime: LocalTime,
    /** Endzeit */
    val endTime: LocalTime,
    /** max Signale 1..X; null = unbegrenzt */
    val maxSignals: Int? = null,
    val priceIndex: PriceIndexParams,
    override val schemaVersion: Int = MMS_SCHEMA_VERSION,
) : StrategyParams

// ---- Options (einziges dynamisches Auswahlfeld ist das Konto) ----------------------

@Serializable
@SerialName("mms")
data class MmsOptions(
    val accounts: List<AccountRef>,
    override val asOf: Instant,
    /**
     * Stückzahl-Deckel aus dem MMS-Lizenz-Tier des Desktops (S/M/L); Formular-Hinweis und
     * Grenze in [validateMms]. `null` = kein Deckel (UNLIMITED) oder alter Desktop ohne
     * dieses Feld — durchgesetzt wird der Deckel in jedem Fall desktopseitig.
     */
    val maxQuantity: Int? = null,
) : StrategyOptions

// ---- Status-Projektion (§8.1: aus MMSState abgeleitet) -----------------------------

/** Kompakte Strike-Anzeige aus dem laufenden Strike-Finder (MMSState.long/shortStrike). */
@Serializable
data class MmsStrikeStatus(
    val symbol: String,
    val bid: Decimal? = null,
    val ask: Decimal? = null,
    /** Mid-Preis innerhalb der Toleranz um den Zielpreis (MMSState.*StrikeValid) */
    val valid: Boolean = true,
)

/**
 * Projektion von `MMSState` (business/strategy/state) auf den Remote-Draht:
 * aufgenommen sind Phase, Kernkennzahlen (SPX, PriceIndex) und die beiden Strikes;
 * weggelassen sind interne Referenzen und die unbegrenzt wachsenden Optionsketten
 * (`puts`/`calls`, 64-KiB-Risiko). Das Mapping StrategyStatus→[StrategyPhase] und
 * XBigNum→[Decimal] macht `app`.
 */
@Serializable
@SerialName("mms")
data class MmsStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    val spxPrice: Decimal? = null,
    val priceIndex: Double? = null,
    val longStrike: MmsStrikeStatus? = null,
    val shortStrike: MmsStrikeStatus? = null,
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = MMS_STRATEGY_ID
}

// ---- Bounds (§3.5): konservative Registry-Defaults ---------------------------------

/**
 * `allowTransmitToExchange = false` als Default ist die konsequente Anwendung von §5:
 * ohne Confirmation-Gate ist die konservativste sinnvolle Grundeinstellung „remote
 * gestartet, aber nicht an die Börse übertragen", bis der Betreiber die Grenze bewusst
 * weitet. Verstoß ⇒ `POLICY_DENIED`.
 */
data class MmsBounds(
    val maxQuantity: Int? = 10,
    val maxSignalsCap: Int = 10,
    val allowTransmitToExchange: Boolean = false,
    /**
     * Getrennte Sanity-Bereiche für Long- und Short-Beine (Abweichung von Spec §3.5,
     * dort ein gemeinsamer `priceSanityRange`): Die Zielpreise liegen in verschiedenen
     * Größenordnungen (Longs ~0.05, Shorts ~1.5) — ein gemeinsamer Bereich könnte nie
     * beides sinnvoll eingrenzen. null = kein Check für die jeweilige Seite.
     */
    val longPriceSanityRange: ClosedRange<Decimal>? = null,
    val shortPriceSanityRange: ClosedRange<Decimal>? = null,
) : ParamsBounds<MmsParams> {

    override fun violations(params: MmsParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: MmsParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (maxQuantity != null && params.quantity > maxQuantity) {
            violations += RemoteText(
                de = "quantity ${params.quantity} überschreitet das Limit $maxQuantity",
                en = "quantity ${params.quantity} exceeds the limit $maxQuantity",
            )
        }
        // null = unbegrenzt — für Remote-Aktivierungen konsequent als Cap-Verstoß behandelt
        if (params.maxSignals == null || params.maxSignals > maxSignalsCap) {
            violations += RemoteText(
                de = "maxSignals ${params.maxSignals ?: "unbegrenzt"} überschreitet das Limit $maxSignalsCap",
                en = "maxSignals ${params.maxSignals ?: "unlimited"} exceeds the limit $maxSignalsCap",
            )
        }
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }

        fun check(range: ClosedRange<Decimal>?, field: String, value: Decimal?) {
            if (range != null && value != null && value !in range) {
                violations += RemoteText(
                    de = "$field $value liegt außerhalb des Sanity-Bereichs ${range.start}..${range.endInclusive}",
                    en = "$field $value is outside the sanity range ${range.start}..${range.endInclusive}",
                )
            }
        }
        check(longPriceSanityRange, "longPut.targetPrice", params.longPut.targetPrice)
        check(longPriceSanityRange, "longCall.targetPrice", params.longCall.targetPrice)
        check(shortPriceSanityRange, "shortPut.targetPrice", params.shortPut.targetPrice)
        check(shortPriceSanityRange, "shortCall.targetPrice", params.shortCall.targetPrice)
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ----------------------------------------------------

/**
 * Strukturelle Invarianten + Options-Mitgliedschaft (§3.5) — läuft mit demselben Code
 * als Live-Formularvalidierung in der SPA und autoritativ im Desktop-Wrapper.
 * `options == null` ⇒ nur strukturelle Prüfungen (SPA-Zustand vor dem describe-Load);
 * der Desktop ruft nie mit `null` auf.
 *
 * Bewusst NICHT kodiert (§8.2 — erst verifizieren, dann kodieren): Ordnung der
 * Schwellen-Tripel, `sma ≤ period`, TP/SL-Obergrenzen. Die Signal-State-Maschine
 * (MMSStrategy.toSignal) funktioniert auch mit „ungeordneten" Schwellen; geratene
 * Invarianten, die legitime Eingaben blockieren, sind schlimmer als fehlende.
 * `startTime < endTime` ist gesichert: die Strategie filtert Signale über
 * `in startTime..endTime` — ein Übernacht-Fenster wäre dort niemals erfüllbar.
 * `zoneStart < zoneEnd` gilt zonenunabhängig als kanonische Form (die Strategie
 * normalisiert per min/max, die Relation trägt keine Bear/Bull-Semantik).
 */
fun validateMms(params: MmsParams, options: MmsOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    fun invalid(field: String, message: String, en: String) {
        errors += ValidationError(field, message, ValidationCode.INVALID, messageText = RemoteText(de = message, en = en))
    }

    if (params.schemaVersion != MMS_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $MMS_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $MMS_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $MMS_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }

    if (params.quantity < 1) invalid("quantity", "Anzahl muss ≥ 1 sein", "Quantity must be ≥ 1")
    // Lizenz-Deckel aus den Options; desktopseitig autoritativ, dieselbe Prüfung als
    // Live-Hinweis im Formular.
    options?.maxQuantity?.let { max ->
        if (params.quantity > max) {
            // Wortlaut wie die Desktop-Meldung (messages.quantityLimitExceeded)
            val (de, en) = if (max == 1) "Kontrakt" to "contract" else "Kontrakte" to "contracts"
            invalid(
                "quantity",
                "Deine Stufe erlaubt $max $de je Trade. Größere Pakete findest Du in Deinem Konto.",
                "Your tier allows $max $en per trade. Larger packages are available in your account.",
            )
        }
    }
    if (params.maxSignals != null && params.maxSignals < 1) invalid("maxSignals", "max. Signale muss ≥ 1 sein", "Max. signals must be ≥ 1")

    // Gesetzte Werte müssen plausibel sein — null („Feld leer") ist strukturell erlaubt,
    // ob ein Bein vollständig sein MUSS, entscheidet die Zone-Prüfung unten.
    fun longLeg(field: String, leg: LongLegParams) {
        if (leg.targetPrice != null && leg.targetPrice.toDouble() <= 0.0) invalid("$field.targetPrice", "Zielprämie muss > 0 sein", "Target premium must be > 0")
        if (leg.maxDeviation != null && leg.maxDeviation < 0.0) invalid("$field.maxDeviation", "Toleranz muss ≥ 0 sein", "Tolerance must be ≥ 0")
    }

    fun shortLeg(field: String, leg: ShortLegParams) {
        if (leg.targetPrice != null && leg.targetPrice.toDouble() <= 0.0) invalid("$field.targetPrice", "Zielprämie muss > 0 sein", "Target premium must be > 0")
        if (leg.maxDeviation != null && leg.maxDeviation < 0.0) invalid("$field.maxDeviation", "Toleranz muss ≥ 0 sein", "Tolerance must be ≥ 0")
        if (leg.takeProfit != null && leg.takeProfit <= 0.0) invalid("$field.takeProfit", "TP muss > 0 sein", "TP must be > 0")
        if (leg.stopLoss != null && leg.stopLoss <= 0.0) invalid("$field.stopLoss", "SL muss > 0 sein", "SL must be > 0")
    }

    longLeg("longPut", params.longPut)
    longLeg("longCall", params.longCall)
    shortLeg("shortPut", params.shortPut)
    shortLeg("shortCall", params.shortCall)

    // Pflichtfelder je aktiver Zone (Bull → Put-Beine, Bear → Call-Beine): das aktive
    // Short-Bein braucht Zielpreis + max. Abweichung, bei autoOpenLongs auch das aktive
    // Long-Bein. Inaktive Beine dürfen leer bleiben.
    fun requireLeg(field: String, targetPrice: Decimal?, maxDeviation: Double?) {
        if (targetPrice == null) invalid("$field.targetPrice", "Zielprämie ist für die aktive Zone erforderlich", "Target premium is required for the active zone")
        if (maxDeviation == null) invalid("$field.maxDeviation", "Toleranz ist für die aktive Zone erforderlich", "Tolerance is required for the active zone")
    }
    if (params.zone == Zone.BULL) {
        requireLeg("shortPut", params.shortPut.targetPrice, params.shortPut.maxDeviation)
        if (params.autoOpenLongs) requireLeg("longPut", params.longPut.targetPrice, params.longPut.maxDeviation)
    } else {
        requireLeg("shortCall", params.shortCall.targetPrice, params.shortCall.maxDeviation)
        if (params.autoOpenLongs) requireLeg("longCall", params.longCall.targetPrice, params.longCall.maxDeviation)
    }

    if (params.priceIndex.period < 1) invalid("priceIndex.period", "Period muss ≥ 1 sein", "Period must be ≥ 1")
    if (params.priceIndex.sma < 1) invalid("priceIndex.sma", "SMA muss ≥ 1 sein", "SMA must be ≥ 1")

    // Der PriceIndex ist per Konstruktion auf [0, 100] normiert (MMSStrategy.toPriceIndex).
    fun thresholds(field: String, triple: ThresholdTriple) {
        listOf("t1" to triple.t1, "t2" to triple.t2, "t3" to triple.t3).forEach { (name, value) ->
            if (value !in 0.0..100.0) invalid("$field.$name", "Schwelle muss in 0..100 liegen", "Threshold must be within 0..100")
        }
    }
    thresholds("priceIndex.longThresholds", params.priceIndex.longThresholds)
    thresholds("priceIndex.shortThresholds", params.priceIndex.shortThresholds)

    if (params.startTime >= params.endTime) invalid("startTime", "Startzeit muss vor der Endzeit liegen", "Start time must be before the end time")
    if (params.zoneStart >= params.zoneEnd) invalid("zoneStart", "Beginn Zone muss kleiner als Ende Zone sein", "Zone start must be less than zone end")

    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }

    return errors
}
